import {type CompPassRecipient} from 'api/comp-passes/comp-pass.types';
import formatPhoneNumber from 'utils/format-phone-number';
import {type FileError} from 'react-dropzone';

export const BETA_COMP_PASS_LIMIT = 500;

interface MaskManualInputValuesProps {
    value: string | number;
    inputType: string;
}

export const maskManualInputValues = ({
    value,
    inputType,
}: MaskManualInputValuesProps) => {
    if (typeof value !== 'string') {
        return value;
    }

    switch (inputType) {
        case 'email': {
            const maskedValue = value.toLowerCase();

            return maskedValue;
        }
        case 'tel': {
            const maskedValue = formatPhoneNumber(value);

            return maskedValue !== '' ? maskedValue : value;
        }
        case 'text': {
            const maskedValue = value.toUpperCase();

            return maskedValue !== '' ? maskedValue : value;
        }
        default: {
            return value;
        }
    }
};

interface CalculateTotalNumberOfPassesProps {
    recipients: CompPassRecipient[];
    numberOfEvents?: number;
}

export const calculateTotalNumberOfPasses = ({
    recipients,
    numberOfEvents,
}: CalculateTotalNumberOfPassesProps) => {
    return recipients.reduce((acc, current) => {
        return (
            acc +
            Number(current.quantity) * (numberOfEvents ? numberOfEvents : 1)
        );
    }, 0);
};

type PaginateArrayProps<T> = {
    data: T[];
    pageNumber: number;
    pageSize: number;
};

export const paginateArray = <T>({
    data,
    pageNumber,
    pageSize,
}: PaginateArrayProps<T>) => {
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = pageNumber * pageSize;

    return data.slice(startIndex, endIndex);
};

interface AnimationPropsArgs {
    show: boolean;
    index?: number;
}

export const animationProps = ({show, index = 0}: AnimationPropsArgs) => {
    const calculatedOffsetY = `-${(index + 1) * 40}px`;
    const calculatedDelay = index * 0.1 + 0.125;

    return {
        in: show,
        unmountOnExit: true,
        offsetY: calculatedOffsetY,
        transition: {
            enter: {
                delay: calculatedDelay,
            },
            ease: 'easeInOut',
        },
    };
};

export const INPUT_NAMES = {
    email: 'email',
    phoneNumber: 'phoneNumber',
    licensePlate: 'licensePlate',
    passesPerEvent: 'quantity',
    ccEmail: 'ccEmail',
    quantity: 'quantity',
};

export const MANUAL_INPUTS = [
    {
        isOptional: false,
        isRequired: true,
        label: 'Email*',
        name: 'email',
        type: 'email',
    },

    {
        isOptional: false,
        isRequired: true,
        label: 'Passes Per Event*',
        name: 'quantity',
        type: 'number',
    },
    {
        isOptional: true,
        isRequired: false,
        label: 'Phone Number',
        name: 'phoneNumber',
        type: 'tel',
    },
    {
        isOptional: true,
        isRequired: false,
        label: 'License Plate',
        name: 'licensePlate',
        type: 'text',
    },
    {
        isOptional: true,
        isRequired: false,
        label: 'CC Email',
        name: 'ccEmail',
        type: 'email',
    },
] as const;

export const ADD_RECIPIENTS_METHOD = {
    UPLOAD: 'upload',
    MANUAL: 'manual',
};

export const DEFAULT_FORM_ROW = {
    email: '',
    phoneNumber: '',
    licensePlate: '',
    quantity: 1,
    ccEmail: '',
} as const;

export const GLOBAL_ERRORS = {
    eventSelection: 'Select an event before moving to the next step.',
    addRecipients: {
        upload: 'Fix errors and re-upload the .csv file  before moving to the next step.',
        manual: 'Fix the highlighted errors before moving to the next step.',
        required:
            'Complete all required fields before moving to the next step.',
    },
};

export const ERROR_BANNER_KEYS = ['upload', 'maxCompPasses', 'required'];

export const getErrorBannerMessage = (error: Partial<FileError>) => {
    switch (error.code) {
        case 'file-invalid-type': {
            return 'File type must be a .csv';
        }
        case 'MissingQuotes':
        case 'UndetectableDelimiter':
        case 'TooFewFields':
        case 'TooManyFields':
        case 'InvalidQuotes': {
            return 'Issue parsing file. Try again.';
        }
        case 'max-comp-passes':
        case 'maxCompPasses': {
            return `Comp Pass limit exceeded. You can create up to ${BETA_COMP_PASS_LIMIT} passes at a time.`;
        }
        case 'noData': {
            return 'No data found in the uploaded file. Please ensure the file contains at least one recipient.';
        }
        default: {
            return 'Upload failed. Try again.';
        }
    }
};

export const COMP_PASS_STATUSES = {
    readyToSend: {
        label: 'Ready to Send',
        id: 'readyToSend',
        description:
            'All required fields have been completed, and the passes for this event are ready to be processed.',
        backgroundColor: 'green.100',
        color: 'text.primary.default',
    },
};
